import { MemorySwitches } from '../components/MemorySwitches'
import { askSensitive } from '../components/SensitiveStep'
import { useRef, useState } from 'react'
import { Link, NavLink, Navigate, Route, Routes } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, FolderGit2, KeyRound, Mail, Pencil, Plus, RefreshCw, ShieldAlert, Trash2, Users, X } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatDate, formatRelative } from '../lib/format'
import { atLeast, getSession, saveSession } from '../lib/auth'
import { Badge, CopyButton, EmptyState, Panel, Segmented, Skeleton } from '../components/primitives'
import { TeamChips } from '../components/TeamChips'
import { CharacterBuilder } from '../components/CharacterBuilder'
import { ProjectAgentActions } from '../components/ProjectAgentActions'
import { IMAGE_RULES, prepareImage } from '../bughunt/scenery'
import type {
  RoomTexts,
  AiModel,
  Avatar as AvatarConfig,
  CompletionPolicy,
  CreatedInvitation,
  TelemetrySettings,
  WorkflowSettings,
  Developer,
  Team,
  TeamKind,
} from '../lib/types'
import Billing from './Billing'
import { LogLevelField } from '../components/LogLevelField'
import { setSimpleMode, useSimpleMode } from '../lib/simpleMode'
import { GoogleSignIn } from '../components/GoogleSignIn'
import { AccountDataPanel } from '../components/AccountData'
import { ConfirmIdentity } from '../components/ConfirmIdentity'
import { TeamWorkSwitch } from '../components/TeamWorkSwitch'

// In groups (docs/refactor/08-configuracoes.md). The account is not here: it is the avatar's, everyone's.
const GROUPS = [
  { key: 'settings.group.organization', tabs: [{ path: 'organization', key: 'settings.organization' }, { path: 'billing', key: 'plans.title' }] },
  { key: 'settings.group.team', tabs: [{ path: 'members', key: 'settings.members' }, { path: 'teams', key: 'teams.title' }, { path: 'developers', key: 'settings.developers' }] },
  { key: 'settings.group.projects', tabs: [{ path: 'projects', key: 'settings.projects' }] },
  {
    key: 'settings.group.advanced',
    tabs: [
      { path: 'workflow', key: 'settings.workflow' },
      { path: 'telemetry', key: 'settings.telemetry' },
      { path: 'credentials', key: 'settings.credentials' },
      { path: 'providers', key: 'settings.providers' },
    ],
  },
]

export default function Settings() {
  const { t } = useI18n()

  return (
    <div className="stack">
      <nav className="settings-groups" aria-label={t('nav.organization')}>
        {GROUPS.map((group) => (
          <div key={group.key} className="settings-groups__group">
            <span className="tiny muted settings-groups__label">{t(group.key)}</span>
            <div className="row row--wrap row--tight">
              {group.tabs.map((tab) => (
                <NavLink
                  key={tab.path}
                  to={`/settings/${tab.path}`}
                  className={({ isActive }) => `segmented__item${isActive ? ' segmented__item--active' : ''}`}
                  style={{ border: '1px solid var(--border)', background: 'var(--bg-panel)' }}
                >
                  {t(tab.key)}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <Routes>
        <Route index element={<Navigate to="organization" replace />} />
        <Route path="organization" element={<OrganizationTab />} />
        <Route path="members" element={<MembersTab />} />
        <Route path="teams" element={<TeamsTab />} />
        <Route path="developers" element={<DevelopersTab />} />
        <Route path="projects" element={<ProjectsTab />} />
        <Route path="providers" element={<ProvidersTab />} />
        <Route path="credentials" element={<CredentialsTab />} />
        <Route path="workflow" element={<WorkflowTab />} />
        <Route path="telemetry" element={<TelemetryTab />} />
        <Route path="billing" element={<Billing />} />
        <Route path="account" element={<AccountTab />} />
      </Routes>
    </div>
  )
}

function OrganizationTab() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data } = useQuery({ queryKey: ['organization'], queryFn: api.settings.organization })
  const [name, setName] = useState('')

  const rename = useMutation({
    mutationFn: (value: string) => api.settings.rename(value),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['organization'] }),
  })

  if (!data) return <Skeleton height={160} />

  return (
    <div className="stack">
      <Panel title={t('settings.organization')}>
        <form
          className="stack"
          style={{ maxWidth: 420 }}
          onSubmit={(event) => {
            event.preventDefault()
            rename.mutate(name || data.name)
          }}
        >
          <label className="field">
            <span className="field__label">{t('settings.org.name')}</span>
            <input className="input" defaultValue={data.name} onChange={(event) => setName(event.target.value)} />
          </label>

          <label className="field">
            <span className="field__label">{t('settings.org.slug')}</span>
            <input className="input mono" value={data.slug} readOnly disabled />
          </label>

          <button type="submit" className="btn btn--primary" disabled={rename.isPending}>
            {rename.isPending ? t('common.saving') : t('common.save')}
          </button>
        </form>
      </Panel>
      <WeeklyReportPanel />
    </div>
  )
}

/**
 * The manager's week by email (docs/refactor/41 §4, feature 7): every Friday afternoon, to the
 * managers, the same numbers and deliveries Acompanhar › Relatórios shows. Part of the Team plan.
 * "Enviar agora" is how a person sees the email before deciding; the result is said honestly.
 */
function WeeklyReportPanel() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: state } = useQuery({ queryKey: ['weeklyReport'], queryFn: api.settings.weeklyReport })
  const { data: plan } = useQuery({ queryKey: ['billing'], queryFn: api.billing.state, staleTime: 60_000 })

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.settings.setWeeklyReport(enabled),
    onSuccess: (next) => queryClient.setQueryData(['weeklyReport'], next),
  })
  const send = useMutation({ mutationFn: api.settings.sendWeeklyReport })

  if (!state) return null
  const teamPlan = !plan || plan.entitlements.managerView

  return (
    <Panel title={<span className="row row--tight"><Mail size={15} aria-hidden /> {t('weekly.title')}</span>}>
      <div className="stack">
        <span className="small muted">{t('weekly.what')}</span>
        {!teamPlan ? (
          <span className="small muted">{t('weekly.teamOnly')} <Link to="billing">{t('plans.upgrade')}</Link></span>
        ) : !state.emailConfigured ? (
          // A server without SMTP is a supported state, said plainly instead of a button that lies.
          <span className="small muted">{t('weekly.emailOff')}</span>
        ) : (
          <>
            <label className="row row--tight">
              <input
                type="checkbox"
                checked={state.enabled}
                onChange={(event) => toggle.mutate(event.target.checked)}
                disabled={toggle.isPending}
              />
              <span className="small">{t('weekly.enable', { count: state.recipients })}</span>
            </label>
            <div className="row row--tight row--wrap">
              <button type="button" className="btn btn--sm" onClick={() => send.mutate()} disabled={send.isPending}>
                <Mail size={13} /> {t('weekly.sendNow')}
              </button>
              {send.data && (
                <span className="small muted">
                  {send.data.status === 'sent' ? t('weekly.sent', { count: send.data.sentTo }) : t(`weekly.${send.data.status}`)}
                </span>
              )}
              {send.isError && <span className="small muted">{t('error.generic')}</span>}
            </div>
            {state.sentAt && <span className="tiny muted">{t('weekly.last', { at: new Date(state.sentAt).toLocaleString() })}</span>}
          </>
        )}
      </div>
    </Panel>
  )
}

/**
 * Teams and squads, shaped by an engineering manager.
 *
 * A squad usually sits inside a team, and a developer can be in several groups at once — their
 * team and a squad in it, or two squads. Everyone in a team's squads counts as in the team when the
 * room or the Bug Hunt is filtered by it. Each developer also has one main team, which is what
 * reports break down by; it is set on the Developers tab, and their first group becomes it.
 */
function TeamsTab() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: teams } = useQuery({ queryKey: ['teams'], queryFn: api.settings.teams })
  const { data: developers } = useQuery({ queryKey: ['developers'], queryFn: api.settings.developers })
  // Linked repositories by team: whoever joins a team with repositories needs git access to them.
  const { data: repositories } = useQuery({ queryKey: ['repositories'], queryFn: api.repositories.list, staleTime: 30_000 })
  const [added, setAdded] = useState<{ teamId: string; developerId: string } | null>(null)
  // Somebody else is asked, not added: they join when they accept it in their inbox (docs/refactor/55).
  const [invited, setInvited] = useState<string | null>(null)

  const [name, setName] = useState('')
  const [kind, setKind] = useState<TeamKind>('Team')
  const [parentId, setParentId] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const refresh = () => {
    setFailure(null)
    for (const key of ['teams', 'developers', 'bug-hunt', 'room-activity', 'reports']) {
      void queryClient.invalidateQueries({ queryKey: [key] })
    }
  }
  const onError = (error: unknown) => {
    const code = error instanceof ApiError ? error.code : undefined
    setFailure(code === 'name_taken' || code === 'invalid_parent' ? t(`teams.error.${code}`) : t('error.generic'))
  }

  const create = useMutation({
    mutationFn: () => api.settings.createTeam({
      name: name.trim(),
      kind,
      parentTeamId: kind === 'Squad' && parentId ? parentId : null,
    }),
    onSuccess: () => {
      setName('')
      refresh()
    },
    onError,
  })

  const rename = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) => api.settings.updateTeam(id, { name: value }),
    onSuccess: refresh,
    onError,
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.settings.deleteTeam(id),
    onSuccess: refresh,
    onError,
  })

  const addMember = useMutation({
    mutationFn: ({ teamId, developerId }: { teamId: string; developerId: string }) => api.settings.addMember(teamId, developerId),
    onSuccess: (result, { developerId }) => {
      const asked = result?.status === 'invited'
      setInvited(asked ? (people.find((d) => d.id === developerId)?.displayName ?? '') : null)
      if (asked) setAdded(null)
      refresh()
    },
    onError,
  })

  const removeMember = useMutation({
    mutationFn: ({ teamId, developerId }: { teamId: string; developerId: string }) => api.settings.removeMember(teamId, developerId),
    onSuccess: refresh,
    onError,
  })

  const topTeams = (teams ?? []).filter((team) => (team.kind ?? 'Team') === 'Team')
  const people = (developers ?? []).filter((d) => d.isActive)
  const nameOf = new Map(people.map((d) => [d.id, d.displayName]))

  return (
    <div className="stack">
      <Panel title={t('teams.title')} subtitle={t('teams.hint')}>
        <form
          className="row row--wrap"
          onSubmit={(event) => {
            event.preventDefault()
            if (name.trim()) create.mutate()
          }}
        >
          <Segmented<TeamKind>
            value={kind}
            options={[
              { value: 'Team', label: t('teams.kind.Team') },
              { value: 'Squad', label: t('teams.kind.Squad') },
            ]}
            onChange={setKind}
          />
          <input
            className="input"
            placeholder={kind === 'Squad' ? t('teams.squadName') : t('teams.teamName')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: 220 }}
            maxLength={200}
            aria-label={kind === 'Squad' ? t('teams.squadName') : t('teams.teamName')}
          />
          {kind === 'Squad' && (
            <label className="row row--tight small">
              <span className="muted">{t('teams.inside')}</span>
              <select className="select select--sm" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                <option value="">{t('teams.standalone')}</option>
                {topTeams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
              </select>
            </label>
          )}
          <button type="submit" className="btn btn--sm btn--primary" disabled={create.isPending || !name.trim()}>
            <Plus size={13} /> {t('teams.create')}
          </button>
        </form>
        {failure && <p className="small" style={{ color: 'var(--danger-text)', margin: 'var(--s2) 0 0' }}>{failure}</p>}
      </Panel>

      {invited !== null && (
        <div className="notice small" role="status">{t('teams.invited', { name: invited })}</div>
      )}
      {!teams ? (
        <Skeleton height={120} />
      ) : teams.length === 0 ? (
        <Panel>
          <EmptyState title={t('teams.empty')} />
        </Panel>
      ) : (
        <div className="teams">
          {teams.map((team) => (
            <TeamCard
              key={team.id}
              team={team}
              parentName={team.parentTeamId ? teams.find((p) => p.id === team.parentTeamId)?.name ?? null : null}
              members={(team.memberIds ?? []).map((id) => ({ id, name: nameOf.get(id) ?? '—' }))}
              candidates={people.filter((d) => !(team.memberIds ?? []).includes(d.id))}
              busy={addMember.isPending || removeMember.isPending || rename.isPending || remove.isPending}
              repositories={(repositories ?? []).filter((r) => r.linked && r.teamId === team.id).map((r) => r.name)}
              justAdded={added?.teamId === team.id ? (people.find((d) => d.id === added.developerId) ?? null) : null}
              onAdd={(developerId) => {
                setAdded({ teamId: team.id, developerId })
                addMember.mutate({ teamId: team.id, developerId })
              }}
              onRemove={(developerId) => removeMember.mutate({ teamId: team.id, developerId })}
              onRename={(value) => rename.mutate({ id: team.id, value })}
              onDelete={() => {
                if (window.confirm(t('teams.deleteConfirm', { name: team.name }))) remove.mutate(team.id)
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function TeamCard({
  team,
  parentName,
  members,
  candidates,
  busy,
  onAdd,
  onRemove,
  onRename,
  onDelete,
  repositories,
  justAdded,
}: {
  team: Team
  parentName: string | null
  /** The linked repositories of this team, by name. */
  repositories: string[]
  /** Somebody just added to this team, for the git-access reminder. */
  justAdded: Developer | null
  members: Array<{ id: string; name: string }>
  candidates: Developer[]
  busy: boolean
  onAdd: (developerId: string) => void
  onRemove: (developerId: string) => void
  onRename: (name: string) => void
  onDelete: () => void
}) {
  const { t } = useI18n()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(team.name)
  const squad = team.kind === 'Squad'

  return (
    <section className={`team-card${squad && parentName ? ' team-card--squad' : ''}`} aria-label={team.name}>
      <header className="row row--tight row--wrap">
        {editing ? (
          <form
            className="row row--tight"
            onSubmit={(event) => {
              event.preventDefault()
              if (draft.trim() && draft.trim() !== team.name) onRename(draft.trim())
              setEditing(false)
            }}
          >
            <input className="input" style={{ height: 28, width: 220 }} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus maxLength={200} aria-label={t('teams.rename')} />
            <button type="submit" className="btn btn--sm btn--ghost" aria-label={t('common.save')}><Check size={13} /></button>
          </form>
        ) : (
          <span className="bold">{team.name}</span>
        )}
        <Badge tone={squad ? 'purple' : 'accent'}>{t(`teams.kind.${squad ? 'Squad' : 'Team'}`)}</Badge>
        {squad && parentName && <span className="tiny muted">{t('teams.insideOf', { name: parentName })}</span>}
        <span className="tiny muted">{t('settings.team.members', { count: members.length })}</span>
        {/* What this team works on. A project belongs to one team, so this is the whole list. */}
        {(team.projects ?? []).map((project) => (
          <span key={project.id} className="team-project" title={project.name}>
            <FolderGit2 size={11} aria-hidden /> {project.key}
          </span>
        ))}
        <div className="spacer" />
        {!editing && (
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => { setDraft(team.name); setEditing(true) }} title={t('teams.rename')} aria-label={t('teams.rename')}>
            <Pencil size={13} />
          </button>
        )}
        <button type="button" className="btn btn--sm btn--ghost" onClick={onDelete} disabled={busy} title={t('teams.delete')} aria-label={t('teams.delete')}>
          <Trash2 size={13} />
        </button>
      </header>

      <div className="team-card__members">
        {members.length === 0 && <span className="tiny muted">{t('teams.noMembers')}</span>}
        {members.map((member) => (
          <span key={member.id} className="team-member">
            {member.name}
            <button
              type="button"
              className="team-member__remove"
              onClick={() => onRemove(member.id)}
              disabled={busy}
              aria-label={t('teams.removeMember', { name: member.name, team: team.name })}
              title={t('teams.removeMember', { name: member.name, team: team.name })}
            >
              <X size={11} />
            </button>
          </span>
        ))}

        {candidates.length > 0 && (
          <select
            className="select select--sm team-card__add"
            value=""
            onChange={(e) => e.target.value && onAdd(e.target.value)}
            disabled={busy}
            aria-label={t('teams.addMember')}
          >
            <option value="">{t('teams.addMember')}</option>
            {candidates.map((developer) => (
              <option key={developer.id} value={developer.id}>{developer.displayName}</option>
            ))}
          </select>
        )}
      </div>

      {/* A team with repositories: joining it means nothing without access to them on GitHub. */}
      {repositories.length > 0 && (
        <p className={`team-card__git tiny${justAdded ? ' is-now' : ''}`} role={justAdded ? 'status' : undefined}>
          <FolderGit2 size={12} aria-hidden />
          <span>
            {justAdded
              ? t('teams.gitAccessNow', {
                  name: justAdded.displayName,
                  repos: repositories.join(', '),
                  email: justAdded.email ?? t('teams.theirEmail'),
                })
              : t('teams.gitAccess', { repos: repositories.join(', ') })}
          </span>
        </p>
      )}
    </section>
  )
}

function DevelopersTab() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const session = getSession()
  const { data } = useQuery({ queryKey: ['developers'], queryFn: api.settings.developers })
  const { data: teams } = useQuery({ queryKey: ['teams'], queryFn: api.settings.teams })
  const { data: projects } = useQuery({ queryKey: ['projects'], queryFn: api.settings.projects })
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me })
  const [editing, setEditing] = useState<string | null>(null)

  const isAdmin = atLeast(session?.role, 'Admin')

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [gitHandle, setGitHandle] = useState('')
  const [teamId, setTeamId] = useState('')

  const saveAvatar = useMutation({
    mutationFn: ({ id, avatar }: { id: string; avatar: AvatarConfig }) => api.settings.setAvatar(id, avatar),
    onSuccess: () => {
      setEditing(null)
      void queryClient.invalidateQueries({ queryKey: ['developers'] })
      void queryClient.invalidateQueries({ queryKey: ['bug-hunt'] })
    },
  })

  const refreshPeople = () => {
    void queryClient.invalidateQueries({ queryKey: ['developers'] })
    void queryClient.invalidateQueries({ queryKey: ['bug-hunt'] })
    // Every report breaks down by team, so a team change invalidates all of them.
    void queryClient.invalidateQueries({ queryKey: ['reports'] })
    // Putting somebody on a project can create the team behind it, and moves the project onto it.
    void queryClient.invalidateQueries({ queryKey: ['teams'] })
    void queryClient.invalidateQueries({ queryKey: ['projects'] })
  }

  const create = useMutation({
    mutationFn: () => api.settings.createDeveloper({
      displayName: name,
      email: email || undefined,
      gitHandle: gitHandle || undefined,
      teamId: teamId || undefined,
    }),
    onSuccess: () => {
      setName('')
      setEmail('')
      setGitHandle('')
      refreshPeople()
    },
  })

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.settings.updateDeveloper(id, body),
    onSuccess: refreshPeople,
  })

  /**
   * A developer's projects are the ones their groups work on. There is no link from a person to a
   * project, and there should not be: reports break down by team (§43), and a second way to belong
   * would be a second answer to "whose work is this".
   */
  const projectsOf = (developer: Developer): Array<{ id: string; key: string; name: string }> => {
    const theirs = new Set([developer.teamId, ...(developer.teams ?? []).map((team) => team.id)].filter(Boolean))
    return (projects ?? []).filter((project) => project.teamId && theirs.has(project.teamId))
  }

  if (!data) return <Skeleton height={200} />

  return (
    <div className="stack">
      {/*
        Developers appear by themselves as agents report work, which is why this screen was a
        read-only list for so long without looking wrong. What it could not do was put anybody in a
        team — and since §43 has reports break down by team precisely so they never break down by
        person, a product where nobody can be assigned to one leaves that breakdown permanently
        reading "Unassigned". The alternative to ranking individuals has to actually work.
      */}
      {isAdmin && (
        <Panel title={t('settings.developer.new')}>
          <form
            className="row row--wrap"
            onSubmit={(event) => {
              event.preventDefault()
              if (name.trim()) create.mutate()
            }}
          >
            <label className="field" style={{ minWidth: 170 }}>
              <span className="field__label">{t('settings.developer.name')}</span>
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} required />
            </label>

            <label className="field" style={{ minWidth: 190 }}>
              <span className="field__label">{t('settings.developer.email')}</span>
              <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>

            <label className="field" style={{ minWidth: 150 }}>
              <span className="field__label">{t('settings.developer.gitHandle')}</span>
              <input className="input mono" value={gitHandle} onChange={(e) => setGitHandle(e.target.value)} />
            </label>

            <label className="field" style={{ minWidth: 160 }}>
              <span className="field__label">{t('settings.developer.team')}</span>
              <select className="select" value={teamId} onChange={(e) => setTeamId(e.target.value)}>
                <option value="">{t('common.unassigned')}</option>
                {(teams ?? []).map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
              </select>
            </label>

            <button type="submit" className="btn btn--primary" style={{ alignSelf: 'flex-end' }} disabled={create.isPending}>
              <Plus size={14} />
              {t('settings.developer.new')}
            </button>
          </form>
        </Panel>
      )}

      <Panel title={t('settings.developers')} flush>
        <table className="table">
          <thead>
            <tr>
              <th>{t('settings.developer.name')}</th>
              <th>{t('settings.developer.email')}</th>
              <th>{t('settings.developer.mainTeam')}</th>
              <th>{t('settings.developer.projects')}</th>
              <th>{t('settings.credentials')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((developer) => {
              const isSelf = developer.id === me?.developerId
              const canEdit = isSelf || isAdmin

              return (
                <tr key={developer.id} style={{ opacity: developer.isActive ? 1 : 0.5 }}>
                  <td className="bold">
                    {developer.displayName}
                    {!developer.isActive && (
                      <span className="tiny muted"> · {t('settings.developer.inactive')}</span>
                    )}
                  </td>
                  <td className="small muted">{developer.email ?? '—'}</td>
                  <td>
                    {isAdmin ? (
                      <select
                        className="select select--sm"
                        value={developer.teamId ?? ''}
                        onChange={(e) => update.mutate({
                          id: developer.id,
                          // The server reads an empty GUID as "no team", which is how a developer
                          // gets taken out of one rather than merely not put into it.
                          body: { teamId: e.target.value || '00000000-0000-0000-0000-000000000000' },
                        })}
                        disabled={update.isPending}
                      >
                        <option value="">{t('common.unassigned')}</option>
                        {(teams ?? []).map((team) => (
                          <option key={team.id} value={team.id}>{team.name}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="small">
                        {developer.teamName ?? <span className="muted">{t('common.unassigned')}</span>}
                      </span>
                    )}
                    {(developer.teams ?? []).length > 1 && (
                      <div style={{ marginTop: 4 }}>
                        <TeamChips teams={developer.teams} />
                      </div>
                    )}
                  </td>
                  {/*
                    A project, not a team: people think in projects, and the team is the thing
                    Vephus groups by. Choosing one puts them on the project's team — and if the
                    project has none, a team named after it is made, because a project somebody
                    works on is a team of at least one.
                  */}
                  <td>
                    <div className="row row--tight row--wrap">
                      {projectsOf(developer).map((project) => (
                        <span key={project.id} className="team-project" title={project.name}>
                          <FolderGit2 size={11} aria-hidden /> {project.key}
                        </span>
                      ))}
                      {isAdmin && (
                        <select
                          className="select select--sm"
                          value=""
                          onChange={(event) => event.target.value && update.mutate({ id: developer.id, body: { projectId: event.target.value } })}
                          disabled={update.isPending}
                          aria-label={t('settings.developer.addProject', { name: developer.displayName })}
                        >
                          <option value="">{t('settings.developer.addProject', { name: developer.displayName })}</option>
                          {(projects ?? [])
                            .filter((project) => !project.isArchived && !projectsOf(developer).some((p) => p.id === project.id))
                            .map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                        </select>
                      )}
                    </div>
                  </td>
                  <td className="small">{developer.activeCredentials}</td>
                  <td className="right">
                    {canEdit && (
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost"
                        onClick={() => setEditing(editing === developer.id ? null : developer.id)}
                      >
                        {t('settings.avatar')}
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        <p className="tiny muted" style={{ padding: 'var(--s3)' }}>{t('settings.developer.teamHint')}</p>
      </Panel>

      {editing && (
        <AvatarEditor
          key={editing}
          developerId={editing}
          initial={(data ?? []).find((d) => d.id === editing)?.avatar ?? null}
          onSave={(avatar) => saveAvatar.mutate({ id: editing, avatar })}
          onCancel={() => setEditing(null)}
          busy={saveAvatar.isPending}
        />
      )}
    </div>
  )
}



function AvatarEditor({
  developerId,
  initial,
  onSave,
  onCancel,
  busy,
}: {
  developerId: string
  /** What the person chose before, so editing starts from it rather than from a stranger. */
  initial: AvatarConfig | null
  onSave: (avatar: AvatarConfig) => void
  onCancel: () => void
  busy: boolean
}) {
  const { t } = useI18n()

  return (
    <Panel title={t('settings.avatar')} subtitle={t('avatar.hint')}>
      <CharacterBuilder developerId={developerId} initial={initial} busy={busy} onSave={onSave} onCancel={onCancel} />
    </Panel>
  )
}

const ROLES = ['Owner', 'Admin', 'EngineeringManager', 'Developer', 'Viewer'] as const

/**
 * Who belongs to this organization, and what they may do.
 *
 * Until this existed the product was multi-tenant but effectively single-user: three endpoints for
 * listing, inviting and re-roling members had been built, tested and deployed, and nothing in the
 * interface reached any of them. An organization could be created and never joined.
 */
function MembersTab() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const session = getSession()
  const isOwner = session?.role === 'Owner'

  const { data } = useQuery({ queryKey: ['members'], queryFn: api.settings.members })
  const { data: invitations } = useQuery({ queryKey: ['invitations'], queryFn: api.settings.invitations })

  const [email, setEmail] = useState('')
  const [role, setRole] = useState('Developer')
  const [issued, setIssued] = useState<CreatedInvitation | null>(null)
  const [error, setError] = useState<string | null>(null)

  const invite = useMutation({
    mutationFn: () => api.settings.createInvitation({ email, role }),
    onSuccess: (created) => {
      // Held so the link can be copied when no email went out. It is a live credential until it
      // is accepted, so it is never written anywhere but this component's state.
      setIssued(created)
      setError(null)
      setEmail('')
      askSensitive('invite')
      void queryClient.invalidateQueries({ queryKey: ['invitations'] })
    },
    onError: (e: Error) => setError(e.message),
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.settings.revokeInvitation(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['invitations'] }),
  })

  const changeRole = useMutation({
    mutationFn: ({ userId, next }: { userId: string; next: string }) => api.settings.setRole(userId, next),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['members'] }),
    onError: (e: Error) => setError(e.message),
  })
  // Kids mode (docs/refactor/48): the adult's switch, for a member who is not an admin.
  const setKids = useMutation({
    mutationFn: ({ userId, enabled }: { userId: string; enabled: boolean }) => api.settings.setKidsMode(userId, enabled),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['members'] }),
    onError: (e: Error) => setError(e.message),
  })

  // Inviting the team is the moment working together starts to matter: the one Team trial
  // (docs/refactor/41 §4.3) is offered right here too, not only in Planos. Same endpoint, same rules.
  const { data: plan } = useQuery({ queryKey: ['billing'], queryFn: api.billing.state, staleTime: 60_000 })
  const trial = useMutation({
    mutationFn: api.billing.trial,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['billing'] }),
  })

  return (
    <div className="stack">
      <Panel title={t('settings.member.invite')}>
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault()
            if (email.trim()) invite.mutate()
          }}
        >
          <div className="row row--wrap">
            <label className="field" style={{ minWidth: 260 }}>
              <span className="field__label">{t('settings.member.email')}</span>
              <input
                className="input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="off"
              />
            </label>

            <label className="field" style={{ minWidth: 170 }}>
              <span className="field__label">{t('settings.member.role')}</span>
              <select className="select" value={role} onChange={(e) => setRole(e.target.value)}>
                {ROLES.filter((r) => r !== 'Owner' || isOwner).map((r) => (
                  <option key={r} value={r}>{t(`role.${r}`)}</option>
                ))}
              </select>
            </label>

            <button type="submit" className="btn btn--primary" style={{ alignSelf: 'flex-end' }} disabled={invite.isPending}>
              <Plus size={14} />
              {t('settings.member.invite')}
            </button>
          </div>

          {/*
            The inviter no longer chooses anybody's password. Saying so here is the difference
            between an administrator understanding this flow and wondering where the password field
            went.
          */}
          <p className="tiny muted">{t('invite.hint')}</p>
          {/* On Team the seats follow who accepts (docs/refactor/55): said before, not discovered on the invoice. */}
          {plan?.plan === 'Team' && plan.status === 'active' && (data?.length ?? 0) >= plan.seats && (
            <p className="tiny muted">{t('invite.seat', { seats: plan.seats })}</p>
          )}

          {error && <p className="tiny" style={{ color: 'var(--danger-text)' }}>{error}</p>}
        </form>
        {plan?.teamTrialAvailable && plan.canManageBilling && (
          <div className="notice small row row--wrap row--between">
            <span>{t('invite.trial')}</span>
            <button type="button" className="btn btn--sm" onClick={() => trial.mutate()} disabled={trial.isPending}>
              <Users size={13} /> {t('plans.trial')}
            </button>
          </div>
        )}
        {trial.isSuccess && trial.data.teamTrialEndsAt && (
          <div className="notice small">{t('plans.trialOn', { at: new Date(trial.data.teamTrialEndsAt).toLocaleDateString() })}</div>
        )}
      </Panel>

      {issued && (
        <Panel title={issued.email}>
          <div className="stack">
            {issued.emailed ? (
              <div className="row row--tight" style={{ color: 'var(--ok-text)' }}>
                <Check size={15} />
                <span className="small">{t('invite.sent')}</span>
              </div>
            ) : (
              <>
                {/*
                  Shown only when the email genuinely did not go. The server reports that honestly
                  rather than assuming, so this is never a false reassurance in either direction.
                */}
                <div className="row row--tight" style={{ color: 'var(--warn-text)' }}>
                  <ShieldAlert size={15} />
                  <span className="small">{t('invite.notSent')}</span>
                </div>
                <div className="code">
                  <div className="code__copy">
                    <CopyButton text={issued.link} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
                  </div>
                  {issued.link}
                </div>
              </>
            )}

            <button type="button" className="btn btn--sm" onClick={() => setIssued(null)}>{t('common.close')}</button>
          </div>
        </Panel>
      )}

      {(invitations?.length ?? 0) > 0 && (
        <Panel title={t('invite.pending')} flush>
          <table className="table">
            <thead>
              <tr>
                <th>{t('settings.member.email')}</th>
                <th>{t('settings.member.role')}</th>
                <th>{t('invite.expires')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(invitations ?? []).map((invitation) => (
                <tr key={invitation.id}>
                  <td className="small bold">{invitation.email}</td>
                  <td><Badge>{t(`role.${invitation.role}`)}</Badge></td>
                  <td className="tiny muted">{formatDate(invitation.expiresAt, locale)}</td>
                  <td className="right">
                    <button
                      type="button"
                      className="btn btn--sm btn--ghost"
                      onClick={() => {
                        if (window.confirm(t('invite.revokeConfirm'))) revoke.mutate(invitation.id)
                      }}
                    >
                      {t('invite.revoke')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      <Panel title={t('settings.members')} flush>
        {!data ? <Skeleton height={140} /> : data.length <= 1 && (invitations?.length ?? 0) === 0 ? (
          <EmptyState title={t('settings.member.empty')} body={t('settings.member.emptyHint')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('settings.member.name')}</th>
                <th>{t('settings.member.email')}</th>
                <th>{t('settings.member.role')}</th>
                <th title={t('settings.member.kidsHint')}>{t('settings.member.kids')}</th>
                <th>{t('settings.member.lastLogin')}</th>
              </tr>
            </thead>
            <tbody>
              {(data ?? []).map((member) => (
                <tr key={member.userId}>
                  <td className="small bold">
                    {member.displayName}
                    {member.userId === session?.userId && (
                      <span className="tiny muted"> · {t('settings.member.you')}</span>
                    )}
                  </td>
                  <td className="small muted">{member.email}</td>
                  <td>
                    {/*
                      Only an owner may re-role, and nobody may re-role themselves — an owner who
                      demoted their own account would lock the organization out of its own settings
                      with no way back in.
                    */}
                    {isOwner && member.userId !== session?.userId ? (
                      <select
                        className="select select--sm"
                        value={member.role}
                        onChange={(e) => changeRole.mutate({ userId: member.userId, next: e.target.value })}
                        disabled={changeRole.isPending}
                      >
                        {ROLES.map((r) => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
                      </select>
                    ) : (
                      <Badge>{t(`role.${member.role}`)}</Badge>
                    )}
                  </td>
                  <td>
                    {!atLeast(member.role, 'Admin') && (
                      <input
                        type="checkbox"
                        checked={member.kidsMode === true}
                        disabled={setKids.isPending}
                        aria-label={`${t('settings.member.kids')}: ${member.displayName}`}
                        title={t('settings.member.kidsHint')}
                        onChange={(e) => setKids.mutate({ userId: member.userId, enabled: e.target.checked })}
                      />
                    )}
                  </td>
                  <td className="tiny muted">
                    {member.lastLoginAt ? formatRelative(member.lastLoginAt, locale) : t('common.never')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!isOwner && <p className="tiny muted" style={{ padding: 'var(--s3)' }}>{t('settings.member.roleHint')}</p>}
      </Panel>
    </div>
  )
}

/**
 * The caller's own password, and nothing else.
 *
 * Every other tab here administers the organization; this one is the only thing a member can do to
 * their own account. It is last in the list and ungated for that reason: an invited member arrives
 * holding a password somebody else chose, and this is where they stop doing that.
 */
/**
 * "Ver minhas respostas na web" (docs/refactor/10-paridade-web-celular.md): the person's own choice to
 * keep their agents' answers so they can read them away from the computer — for them alone, a week.
 * When the organization keeps answers anyway, it says so instead of offering a switch that adds nothing.
 */
function MyRepliesPanel() {
  const { t } = useI18n()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  if (!me?.developerId) return null
  // The AI memory (docs/refactor/17) holds the person's answers too; the older switch shows only with it off.
  return (
    <Panel title={t('memory.panel')} subtitle={t('memory.hint')}>
      <div className="stack" style={{ gap: 10 }}>
        <MemorySwitches />
      </div>
    </Panel>
  )
}

/**
 * Google connected to a password account from here, and only from here (docs/refactor/54): at sign-in,
 * a Google address matching a password account is refused, because sign-up asks for no proof of the
 * address and the account there could be somebody else's, made first.
 */
function GoogleLinkPanel() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const { data: config } = useQuery({ queryKey: ['auth-config'], queryFn: api.auth.config, staleTime: Infinity })
  const link = useMutation({
    mutationFn: (idToken: string) => api.auth.linkGoogle(idToken),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['me'] }),
  })
  if (!me?.userId || me.isAgent || !config?.googleClientId) return null
  return (
    <Panel title={t('account.google.title')}>
      {me.hasGoogle ? (
        <p className="small" style={{ margin: 0 }}>{t('account.google.linked')}</p>
      ) : (
        <div className="stack">
          <p className="small muted" style={{ margin: 0 }}>{t('account.google.hint')}</p>
          <GoogleSignIn clientId={config.googleClientId} onCredential={(idToken) => link.mutate(idToken)} disabled={link.isPending} />
          {link.isSuccess && <p className="small" role="status" style={{ margin: 0 }}>{t('account.google.done')}</p>}
          {link.isError && (
            <p className="tiny" role="alert" style={{ margin: 0 }}>
              {link.error instanceof ApiError && link.error.code === 'email_mismatch' ? t('account.google.mismatch') : t('error.generic')}
            </p>
          )}
        </div>
      )}
    </Panel>
  )
}

/** Modo simples (docs/refactor/51, N5), switched on or off by the person, on this device. */
function SimpleModePanel() {
  const { t } = useI18n()
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.auth.me, staleTime: 5 * 60_000 })
  const on = useSimpleMode()
  // Kids mode is the admin's lighter Vephus for a child; it already is the simple one.
  if (!me?.userId || me.isAgent || me.kidsMode) return null
  return (
    <Panel title={t('simple.title')}>
      <label className="row row--tight" style={{ alignItems: 'flex-start' }}>
        <input type="checkbox" checked={on} onChange={(event) => setSimpleMode(me.userId!, event.target.checked)} />
        <span className="stack" style={{ gap: 2 }}>
          <span>{t('simple.label')}</span>
          <span className="tiny muted">{t('simple.hint')}</span>
        </span>
      </label>
    </Panel>
  )
}

function AccountTab() {
  const { t } = useI18n()
  const session = getSession()

  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const change = useMutation({
    mutationFn: () => api.auth.changePassword(current, next),
    // Every other session ended with the change; this one continues with the new token.
    onSuccess: (result) => {
      saveSession(result)
      setDone(true)
      setError(null)
      setCurrent('')
      setNext('')
      setConfirm('')
    },
    onError: (e: Error) => {
      setError(e.message)
      setDone(false)
    },
  })

  // Ending the other sessions asks for a recent sign-in, as switching remote control on does (SEC2-02).
  const [confirmingEnd, setConfirmingEnd] = useState(false)
  const endOthers = useMutation({
    mutationFn: api.auth.endOtherSessions,
    onSuccess: (result) => {
      setConfirmingEnd(false)
      saveSession(result)
    },
    onError: (failure) => {
      if (failure instanceof ApiError && failure.code === 'reauth_required') setConfirmingEnd(true)
    },
  })

  return (
    <div className="stack">
    <SimpleModePanel />
    <GoogleLinkPanel />
    <MyRepliesPanel />
    <Panel title={t('auth.changePassword')}>
      <form
        className="stack"
        style={{ maxWidth: 420 }}
        onSubmit={(event) => {
          event.preventDefault()
          if (next !== confirm) {
            setError(t('auth.passwordMismatch'))
            return
          }
          change.mutate()
        }}
      >
        <div className="row row--tight">
          <span className="small bold">{session?.displayName}</span>
          <span className="tiny muted">{session?.email}</span>
        </div>

        <label className="field">
          <span className="field__label">{t('auth.currentPassword')}</span>
          <input
            className="input"
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            autoComplete="current-password"
          />
        </label>

        <label className="field">
          <span className="field__label">{t('auth.newPassword')}</span>
          <input
            className="input"
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            minLength={12}
            required
            autoComplete="new-password"
          />
          <span className="field__hint">{t('auth.passwordHint')}</span>
        </label>

        <label className="field">
          <span className="field__label">{t('auth.confirmPassword')}</span>
          <input
            className="input"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            minLength={12}
            required
            autoComplete="new-password"
          />
        </label>

        <div className="row row--tight">
          <button type="submit" className="btn btn--primary" disabled={change.isPending}>
            {t('auth.changePassword')}
          </button>
          {done && <span className="tiny" style={{ color: 'var(--ok-text)' }}>{t('auth.passwordChanged')}</span>}
          {error && <span className="tiny" style={{ color: 'var(--danger-text)' }}>{error}</span>}
        </div>
      </form>

      <div className="stack stack--tight account__sessions">
        <span className="field__label">{t('auth.sessions.title')}</span>
        <span className="tiny muted">{t('auth.sessions.hint')}</span>
        <div className="row row--tight row--wrap">
          <button type="button" className="btn btn--sm" onClick={() => endOthers.mutate()} disabled={endOthers.isPending}>
            {t('auth.sessions.endOthers')}
          </button>
          {endOthers.isSuccess && <span className="tiny" style={{ color: 'var(--ok-text)' }}>{t('auth.sessions.ended')}</span>}
          {endOthers.isError && !confirmingEnd && <span className="tiny" style={{ color: 'var(--danger-text)' }}>{t('error.generic')}</span>}
        </div>
        {confirmingEnd && <ConfirmIdentity why={t('auth.reauth.whyEndSessions')} onConfirmed={() => endOthers.mutate()} onCancel={() => setConfirmingEnd(false)} />}
      </div>
    </Panel>
    {/* Who may send commands to my agents (docs/refactor/63): always here, whatever else hides it. */}
    <AccountTeamWork />
    {/* A copy of the data, a pause, the end (docs/refactor/65): last, and always here. */}
    <AccountDataPanel />
    </div>
  )
}

/** "Colegas mandam para mim" in Conta: the place it is always found, off by default. */
function AccountTeamWork() {
  const { data } = useQuery({ queryKey: ['remote'], queryFn: api.remote.overview, retry: false })
  if (!data) return null
  return <TeamWorkSwitch accepting={data.acceptTeamWork ?? false} />
}

function ProjectsTab() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data: projects } = useQuery({ queryKey: ['projects'], queryFn: api.settings.projects })
  // The same list (and cache key) as the header and Repositórios e projetos: one source of truth.
  const { data: repositories } = useQuery({ queryKey: ['repositories'], queryFn: api.repositories.list })
  const { data: teamsForProjects } = useQuery({ queryKey: ['teams'], queryFn: api.settings.teams })

  const updateProject = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) => api.settings.updateProject(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['teams'] })
      // Every report breaks down by team, so whose project it is changes all of them.
      void queryClient.invalidateQueries({ queryKey: ['reports'] })
    },
  })

  const [projectName, setProjectName] = useState('')
  const [projectKey, setProjectKey] = useState('')

  const createProject = useMutation({
    mutationFn: () => api.settings.createProject({ name: projectName, key: projectKey.toUpperCase() }),
    onSuccess: () => {
      setProjectName('')
      setProjectKey('')
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  return (
    <div className="grid grid--two">
      <Panel title={t('settings.projects')} flush>
        {/*
          Projects and repositories appear on their own as agents report them, which is why this
          screen was read-only for so long without anyone noticing. But an organization that wants
          to name a project before any agent touches it had no way to, and a repository nobody has
          worked in yet could not be registered at all.
        */}
        <form
          className="row row--wrap"
          style={{ padding: 'var(--s3)', borderBottom: '1px solid var(--border)' }}
          onSubmit={(event) => {
            event.preventDefault()
            if (projectName.trim() && projectKey.trim()) createProject.mutate()
          }}
        >
          <label className="field" style={{ minWidth: 160 }}>
            <span className="field__label">{t('settings.project.name')}</span>
            <input className="input" value={projectName} onChange={(e) => setProjectName(e.target.value)} required />
          </label>
          <label className="field" style={{ minWidth: 110 }}>
            <span className="field__label">{t('settings.project.key')}</span>
            <input
              className="input mono"
              value={projectKey}
              onChange={(e) => setProjectKey(e.target.value.toUpperCase())}
              placeholder="KNIT"
              maxLength={12}
              required
            />
          </label>
          <button type="submit" className="btn btn--sm btn--primary" style={{ alignSelf: 'flex-end' }} disabled={createProject.isPending}>
            <Plus size={14} />
            {t('settings.project.new')}
          </button>
        </form>

        <p className="tiny muted" style={{ padding: '0 var(--s3) var(--s2)' }}>{t('settings.project.teamHint')}</p>

        {!projects ? <Skeleton height={120} /> : projects.length === 0 ? <EmptyState title={t('common.noResults')} /> : (
          <table className="table">
            <tbody>
              {projects.map((project) => (
                <tr key={project.id}>
                  <td className="mono small bold">{project.key}</td>
                  <td>
                    <div className="stack stack--tight" style={{ alignItems: 'flex-start' }}>
                      <span>{project.name}</span>
                      {/* Whose project it is. Reports, the room and the filters all group by team. */}
                      <label className="row row--tight tiny muted">
                        {t('settings.developer.team')}
                        <select
                          className="select select--sm"
                          value={project.teamId ?? ''}
                          onChange={(event) => updateProject.mutate({
                            id: project.id,
                            // An empty GUID is "no team", the same as everywhere else here.
                            body: { teamId: event.target.value || '00000000-0000-0000-0000-000000000000' },
                          })}
                          disabled={updateProject.isPending}
                          aria-label={t('settings.project.team', { project: project.name })}
                        >
                          <option value="">{t('common.unassigned')}</option>
                          {(teamsForProjects ?? []).map((team) => (
                            <option key={team.id} value={team.id}>{team.name}</option>
                          ))}
                        </select>
                      </label>
                      {/* Vephus cannot read the repository; the person's own AI can, and does both of these. */}
                      <ProjectAgentActions project={project} />
                    </div>
                  </td>
                  <td className="right small muted">{project.repositoryCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {/* Repositories are linked in one place only (Agentes › Repositórios e projetos), with the
          checks that go with it: no credentials in the URL, a team, a project. Here, where each
          one is, for reference. */}
      <Panel
        title={t('settings.repositories')}
        flush
        actions={
          <Link to="/agents/repositories" className="btn btn--sm btn--primary">
            <Plus size={14} /> {t('repos.link')}
          </Link>
        }
      >
        {!repositories ? <Skeleton height={120} /> : repositories.filter((r) => r.linked).length === 0 ? <EmptyState title={t('common.noResults')} /> : (
          <table className="table">
            <tbody>
              {repositories.filter((r) => r.linked).map((repository) => (
                <tr key={repository.id}>
                  <td className="mono small">{repository.name}</td>
                  <td className="small">{repository.projectKey ?? t('git.noProject')}</td>
                  <td className="small muted">{repository.teamName ?? '—'}</td>
                  <td className="right small muted">{repository.defaultBranch ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}

function ProvidersTab() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data } = useQuery({ queryKey: ['providers'], queryFn: api.settings.providers })

  if (!data) return <Skeleton height={200} />

  return (
    <div className="stack">
      {/*
        Prices are seeded, and a seeded price goes stale the week a vendor changes its rates. Every
        cost figure in the product is derived from these three numbers, so leaving them read-only
        meant an organization could watch its reported spend drift from its invoice with no way to
        correct it. §8 of the brief asks that cost always be labelled by accuracy, and "estimated
        from a number you cannot edit" is not a defensible kind of estimate.
      */}
      <p className="tiny muted">{t('work.pricingHint')}</p>

      {data.map((provider) => (
        <Panel
          key={provider.id}
          title={
            <span className="row row--tight">
              {provider.displayName}
              {!provider.reportsCost && <Badge tone="neutral">{t('cost.Estimated')}</Badge>}
            </span>
          }
          flush
        >
          <table className="table">
            <thead>
              <tr>
                <th>{t('work.model')}</th>
                <th className="right">Input / 1M</th>
                <th className="right">Output / 1M</th>
                <th className="right">Cached / 1M</th>
              </tr>
            </thead>
            <tbody>
              {provider.models.map((model) => (
                <ModelRow
                  key={model.id}
                  model={model}
                  onSaved={() => void queryClient.invalidateQueries({ queryKey: ['providers'] })}
                />
              ))}
            </tbody>
          </table>
        </Panel>
      ))}
    </div>
  )
}

/**
 * One model's prices, saved on blur.
 *
 * No save button: three numbers in a table row, each independently correctable, and a button per
 * row would be more chrome than content. A field that has not changed does not issue a request.
 */
function ModelRow({ model, onSaved }: { model: AiModel; onSaved: () => void }) {
  const { t } = useI18n()
  const [draft, setDraft] = useState({
    inputPricePerMillion: model.inputPricePerMillion,
    outputPricePerMillion: model.outputPricePerMillion,
    cachedInputPricePerMillion: model.cachedInputPricePerMillion,
  })

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.settings.updateModel(model.id, body),
    onSuccess: onSaved,
  })

  const field = (key: keyof typeof draft) => (
    <td className="right">
      <input
        className="input input--price"
        type="number"
        min={0}
        step="0.01"
        value={draft[key] ?? ''}
        onChange={(event) => {
          const raw = event.target.value
          setDraft((d) => ({ ...d, [key]: raw === '' ? null : Number(raw) }))
        }}
        onBlur={() => {
          if (draft[key] === model[key]) return
          save.mutate({ ...draft, [key]: draft[key] })
        }}
        placeholder="—"
      />
    </td>
  )

  return (
    <tr>
      <td className="mono small">{model.slug}</td>
      {field('inputPricePerMillion')}
      {field('outputPricePerMillion')}
      {field('cachedInputPricePerMillion')}
      {/* The catalogue is one for every organization: only the platform sets prices (docs/refactor/54). */}
      {save.isError && (
        <td className="tiny" role="alert">
          {save.error instanceof ApiError && save.error.code === 'platform_only' ? t('settings.models.platformOnly') : t('error.generic')}
        </td>
      )}
    </tr>
  )
}

function CredentialsTab() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const { data } = useQuery({ queryKey: ['credentials'], queryFn: api.settings.credentials })
  const { data: developers } = useQuery({ queryKey: ['developers'], queryFn: api.settings.developers })

  const [issued, setIssued] = useState<{ key: string; name: string } | null>(null)
  const [developerId, setDeveloperId] = useState('')
  const [name, setName] = useState('')
  const [providerSlug, setProviderSlug] = useState('claude-code')

  const issue = useMutation({
    mutationFn: () => api.settings.issueCredential({ developerId, name, providerSlug, expiresInDays: 365 }),
    onSuccess: (result) => {
      setIssued({ key: result.key, name: result.name })
      setName('')
      void queryClient.invalidateQueries({ queryKey: ['credentials'] })
    },
  })

  const revoke = useMutation({
    mutationFn: (id: string) => api.settings.revokeCredential(id, 'Revoked from settings'),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['credentials'] }),
  })

  // Rotation returns a new key exactly as issuing does, so it reuses the same one-time panel. A
  // key that may have leaked should be replaceable without the agent losing its identity, its
  // history or its developer — which is what revoking and issuing a fresh one would cost.
  const rotate = useMutation({
    mutationFn: (id: string) => api.settings.rotateCredential(id),
    onSuccess: (result) => {
      setIssued({ key: result.key, name: result.name })
      void queryClient.invalidateQueries({ queryKey: ['credentials'] })
    },
  })

  return (
    <div className="stack">
      {issued && (
        <Panel title={t('credentials.keyLabel')}>
          <div className="stack">
            {/* Shown exactly once. The server stores a hash; there is no second chance. */}
            <div className="row row--tight" style={{ color: 'var(--warn-text)' }}>
              <ShieldAlert size={15} />
              <span className="small">{t('credentials.shownOnce')}</span>
            </div>
            <div className="code">
              <div className="code__copy">
                <CopyButton text={issued.key} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
              </div>
              {issued.key}
            </div>
            <button type="button" className="btn btn--sm" onClick={() => setIssued(null)}>{t('common.close')}</button>
          </div>
        </Panel>
      )}

      <Panel title={t('credentials.issue')}>
        <form
          className="row row--wrap"
          onSubmit={(event) => {
            event.preventDefault()
            if (developerId && name.trim()) issue.mutate()
          }}
        >
          <label className="field" style={{ minWidth: 180 }}>
            <span className="field__label">{t('credentials.developer')}</span>
            <select className="select" value={developerId} onChange={(e) => setDeveloperId(e.target.value)} required>
              <option value="">—</option>
              {(developers ?? []).map((developer) => (
                <option key={developer.id} value={developer.id}>{developer.displayName}</option>
              ))}
            </select>
          </label>

          <label className="field" style={{ minWidth: 180 }}>
            <span className="field__label">{t('credentials.name')}</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="MacBook Pro" required />
          </label>

          <label className="field" style={{ minWidth: 160 }}>
            <span className="field__label">{t('credentials.provider')}</span>
            <select className="select" value={providerSlug} onChange={(e) => setProviderSlug(e.target.value)}>
              <option value="claude-code">Claude Code</option>
              <option value="cursor">Cursor</option>
              <option value="kiro">Kiro</option>
            </select>
          </label>

          <button type="submit" className="btn btn--primary" style={{ alignSelf: 'flex-end' }} disabled={issue.isPending}>
            <KeyRound size={14} />
            {t('credentials.issue')}
          </button>
        </form>
      </Panel>

      <Panel title={t('settings.credentials')} flush>
        {!data ? <Skeleton height={120} /> : data.length === 0 ? (
          <EmptyState title={t('credentials.empty')} body={t('credentials.emptyHint')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('credentials.name')}</th>
                <th>{t('credentials.developer')}</th>
                <th>{t('credentials.provider')}</th>
                <th>{t('credentials.lastUsed')}</th>
                <th>{t('credentials.expires')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.map((credential) => (
                <tr key={credential.id} style={{ opacity: credential.revokedAt ? 0.5 : 1 }}>
                  <td>
                    <div className="stack" style={{ gap: 0 }}>
                      <span className="small bold">{credential.name}</span>
                      {/* The prefix is not secret; it is what makes lookup a single indexed read. */}
                      <span className="mono tiny muted">{credential.keyPrefix}…</span>
                    </div>
                  </td>
                  <td className="small">{credential.developerName}</td>
                  <td className="small muted">{credential.providerSlug ?? '—'}</td>
                  <td className="tiny muted">
                    {credential.lastUsedAt ? formatRelative(credential.lastUsedAt, locale) : t('common.never')}
                  </td>
                  <td className="tiny muted">
                    {credential.expiresAt ? formatDate(credential.expiresAt, locale) : '—'}
                  </td>
                  <td className="right">
                    {credential.revokedAt ? (
                      <Badge tone="danger">{t('credentials.revoked')}</Badge>
                    ) : (
                      <div className="row row--tight" style={{ justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost"
                        title={t('credentials.rotate')}
                        onClick={() => {
                          if (window.confirm(t('credentials.rotateConfirm'))) rotate.mutate(credential.id)
                        }}
                        disabled={rotate.isPending}
                      >
                        <RefreshCw size={14} />
                        {t('credentials.rotate')}
                      </button>
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost"
                        onClick={() => {
                          if (window.confirm(t('credentials.revokeConfirm'))) revoke.mutate(credential.id)
                        }}
                      >
                        {t('credentials.revoke')}
                      </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  )
}

const POLICIES: CompletionPolicy[] = ['ReadyForReview', 'PullRequestMerged', 'ExternalTaskProvider', 'AiCompletion']

/** The agent permission switches, with the server's defaults for an organization that never set them. */
const AGENT_PERMISSIONS = [
  { field: 'agentsMaySendWork', key: 'agentsMaySendWork', fallback: true },
  { field: 'agentsMayReadReports', key: 'agentsMayReadReports', fallback: true },
  { field: 'agentsMayManageRepositories', key: 'agentsMayManageRepositories', fallback: false },
  { field: 'agentsMayManageTeams', key: 'agentsMayManageTeams', fallback: false },
] as const satisfies readonly { field: keyof WorkflowSettings; key: string; fallback: boolean }[]

function WorkflowTab() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data } = useQuery({ queryKey: ['workflow'], queryFn: api.settings.workflow })
  const [draft, setDraft] = useState<WorkflowSettings | null>(null)

  const save = useMutation({
    mutationFn: (settings: WorkflowSettings) => api.settings.saveWorkflow(settings),
    onSuccess: () => {
      setDraft(null)
      void queryClient.invalidateQueries({ queryKey: ['workflow'] })
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      void queryClient.invalidateQueries({ queryKey: ['room-texts'] })
      void queryClient.invalidateQueries({ queryKey: ['room', 'scenery'] })
    },
  })

  if (!data) return <Skeleton height={240} />
  const current = draft ?? data
  const update = (patch: Partial<WorkflowSettings>): void => setDraft({ ...current, ...patch })

  return (
    <Panel title={t('settings.workflow')}>
      <div className="stack stack--loose" style={{ maxWidth: 640 }}>
        <div className="stack stack--tight">
          <span className="field__label">{t('workflow.completionPolicy')}</span>
          <span className="field__hint">{t('workflow.completionPolicyHint')}</span>

          {POLICIES.map((policy) => (
            <label key={policy} className="checkbox" style={{ padding: 'var(--s2)' }}>
              <input
                type="radio"
                name="policy"
                checked={current.bugCompletionPolicy === policy}
                onChange={() => update({ bugCompletionPolicy: policy })}
              />
              <span className="checkbox__text">
                <span className="small bold">{t(`workflow.policy.${policy}`)}</span>
                <span className="tiny muted">{t(`workflow.policy.${policy}Hint`)}</span>
              </span>
            </label>
          ))}
        </div>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={current.applyCompletionPolicyToAllTaskTypes}
            onChange={(e) => update({ applyCompletionPolicyToAllTaskTypes: e.target.checked })}
          />
          <span className="checkbox__text">
            <span className="small">{t('workflow.applyToAll')}</span>
            <span className="tiny muted">{t('workflow.applyToAllHint')}</span>
          </span>
        </label>

        <div className="stack stack--tight">
          <span className="field__label">{t('workflow.unknownTask')}</span>
          {(['AllowDraftTask', 'RejectUnknownTask'] as const).map((policy) => (
            <label key={policy} className="checkbox">
              <input
                type="radio"
                name="unknown"
                checked={current.unknownTaskPolicy === policy}
                onChange={() => update({ unknownTaskPolicy: policy })}
              />
              <span className="checkbox__text small">{t(`workflow.unknown.${policy}`)}</span>
            </label>
          ))}
        </div>

        <label className="checkbox">
          <input type="checkbox" checked={current.bugHuntEnabled} onChange={(e) => update({ bugHuntEnabled: e.target.checked })} />
          <span className="checkbox__text">
            <span className="small">{t('workflow.bugHuntEnabled')}</span>
            <span className="tiny muted">{t('workflow.bugHuntEnabledHint')}</span>
          </span>
        </label>

        {/*
          Enforced by the server: a developer set to "only themselves" does not receive their
          colleagues' data at all, rather than receiving it and having it hidden.
        */}
        <div className="stack stack--tight">
          <span className="field__label">{t('workflow.visibility')}</span>
          <span className="field__hint">{t('workflow.visibilityHint')}</span>
          {(['Team', 'TeamWithoutOthersPrompts', 'OnlySelf'] as const).map((option) => (
            <label key={option} className="checkbox">
              <input
                type="radio"
                name="visibility"
                checked={(current.teamVisibility ?? 'Team') === option}
                onChange={() => update({ teamVisibility: option })}
              />
              <span className="checkbox__text">
                <span className="small">{t(`workflow.visibility.${option}`)}</span>
              </span>
            </label>
          ))}
        </div>

        {/*
          Only the switch that makes it possible. Each developer still turns it on for themselves, for
          a few hours, and only for their own agents: nobody here can send commands to anyone.
        */}
        <label className="checkbox">
          <input
            type="checkbox"
            checked={current.allowRemoteControl ?? false}
            onChange={(e) => update({ allowRemoteControl: e.target.checked })}
          />
          <span className="checkbox__text">
            <span className="small">{t('workflow.allowRemoteControl')}</span>
            <span className="tiny muted">{t('workflow.allowRemoteControlHint')}</span>
          </span>
        </label>

        {/*
          What an agent key may do on its own through the MCP — the web version with no desktop app.
          Enforced by the server; an agent refused here is told which switch it was and where it is.
          Credentials, billing, members and the admin area stay with people whatever these say.
        */}
        <div className="stack stack--tight">
          <span className="field__label">{t('workflow.agentPermissions')}</span>
          <span className="field__hint">{t('workflow.agentPermissionsHint')}</span>
          {AGENT_PERMISSIONS.map(({ field, key, fallback }) => (
            <label key={field} className="checkbox">
              <input type="checkbox" checked={current[field] ?? fallback} onChange={(e) => update({ [field]: e.target.checked })} />
              <span className="checkbox__text">
                <span className="small">{t(`workflow.${key}`)}</span>
                <span className="tiny muted">{t(`workflow.${key}Hint`)}</span>
              </span>
            </label>
          ))}
        </div>

        <RoomTextsEditor value={current.roomTexts ?? null} onChange={(roomTexts) => update({ roomTexts })} />

        <label className="checkbox">
          <input
            type="checkbox"
            checked={current.seasonalDecorations ?? true}
            onChange={(e) => update({ seasonalDecorations: e.target.checked })}
          />
          <span className="checkbox__text">
            <span className="small">{t('workflow.seasonalDecorations')}</span>
            <span className="tiny muted">{t('workflow.seasonalDecorationsHint')}</span>
          </span>
        </label>

        <RoomBackgroundEditor />

        <button type="button" className="btn btn--primary" onClick={() => save.mutate(current)} disabled={save.isPending || !draft}>
          {save.isPending ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </Panel>
  )
}

/**
 * The organization's picture for the back of the room.
 *
 * Shrunk and re-encoded in the browser before it is sent, so what is stored is a modest JPEG made
 * of pixels the browser decoded; the server checks the bytes again regardless. Every viewer can
 * still pick another look for themselves — this is one of the options, not an imposition.
 */
function RoomBackgroundEditor() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [note, setNote] = useState<string | null>(null)

  const { data: scenery } = useQuery({ queryKey: ['room', 'scenery'], queryFn: api.room.scenery })
  const version = scenery?.imageVersion ?? null
  const { data: image } = useQuery({
    queryKey: ['room', 'background', version],
    queryFn: api.room.background,
    enabled: version != null,
    staleTime: Infinity,
  })

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['room'] })

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const prepared = await prepareImage(file)
      if (prepared.blob.size > IMAGE_RULES.maxBytes) throw new Error('too_large')
      const small = prepared.width < IMAGE_RULES.minWidth || prepared.height < IMAGE_RULES.minHeight
      await api.room.setBackground(prepared.blob, prepared.width, prepared.height)
      return small
    },
    onSuccess: (small) => {
      setNote(small ? t('scenery.small') : null)
      refresh()
    },
    onError: (error) => {
      const code = error instanceof ApiError ? error.code : error instanceof Error ? error.message : undefined
      setNote(code === 'too_large' ? t('scenery.error.size') : t('scenery.error.type'))
    },
  })

  const remove = useMutation({ mutationFn: api.room.removeBackground, onSuccess: refresh })

  return (
    <div className="stack stack--tight">
      <span className="field__label">{t('scenery.org.title')}</span>
      <span className="field__hint">{t('scenery.org.hint')}</span>

      <ul className="tiny muted" style={{ margin: 0, paddingLeft: 'var(--s5)' }}>
        <li>{t('scenery.rule.format')}</li>
        <li>{t('scenery.rule.size', { width: IMAGE_RULES.idealWidth, height: IMAGE_RULES.idealHeight, minWidth: IMAGE_RULES.minWidth, minHeight: IMAGE_RULES.minHeight })}</li>
        <li>{t('scenery.rule.layout')}</li>
        <li>{t('scenery.rule.content')}</li>
      </ul>

      {image && <img className="backdrop-preview" src={image} alt={t('scenery.org.preview')} />}
      {version != null && scenery?.imageWidth ? (
        <span className="tiny muted">{t('scenery.org.current', { width: scenery.imageWidth, height: scenery.imageHeight ?? 0 })}</span>
      ) : null}

      <div className="row row--tight">
        <button type="button" className="btn btn--sm" onClick={() => fileRef.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? t('common.saving') : version != null ? t('scenery.org.replace') : t('scenery.org.choose')}
        </button>
        {version != null && (
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => remove.mutate()} disabled={remove.isPending}>
            <Trash2 size={13} /> {t('scenery.org.remove')}
          </button>
        )}
      </div>
      {note && <span className="scenery-note" role="status">{note}</span>}

      <input
        ref={fileRef}
        type="file"
        accept={IMAGE_RULES.types.join(',')}
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0]
          event.target.value = ''
          setNote(null)
          if (!file) return
          if (!(IMAGE_RULES.types as readonly string[]).includes(file.type)) {
            setNote(t('scenery.error.type'))
            return
          }
          upload.mutate(file)
        }}
      />
    </div>
  )
}

/** The size of each sign, in characters. Kept in step with RoomTexts on the server. */
const ROOM_LIMITS = { neon: [3, 22], motto: [4, 12], routine: [4, 20], title: 20 } as const

/**
 * The words on the room's walls.
 *
 * Empty means the product's own wording, in each reader's language; the placeholders show what
 * that is. Anything typed here is shown to everyone as written, in one language — the organization's
 * call to make.
 */
function RoomTextsEditor({ value, onChange }: { value: RoomTexts | null; onChange: (texts: RoomTexts | null) => void }) {
  const { t } = useI18n()
  const texts: RoomTexts = value ?? { neon: [], motto: [], routineTitle: null, routine: [] }

  const set = (field: 'neon' | 'motto' | 'routine', index: number, text: string): void => {
    const [count] = ROOM_LIMITS[field]
    const list = Array.from({ length: count }, (_, i) => texts[field]?.[i] ?? '')
    list[index] = text
    onChange({ ...texts, [field]: list })
  }

  const row = (field: 'neon' | 'motto' | 'routine', prefix: string) => {
    const [count, max] = ROOM_LIMITS[field]
    return (
      <div className="grid" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
        {Array.from({ length: count }, (_, i) => (
          <input
            key={i}
            className="input"
            maxLength={max}
            value={texts[field]?.[i] ?? ''}
            placeholder={t(`${prefix}${i + 1}`)}
            onChange={(event) => set(field, i, event.target.value)}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="stack stack--tight">
      <span className="field__label">{t('workflow.roomTexts')}</span>
      <span className="field__hint">{t('workflow.roomTextsHint')}</span>

      <span className="tiny muted">{t('workflow.roomNeon')}</span>
      {row('neon', 'bugHunt.room.neon')}

      <span className="tiny muted">{t('workflow.roomMotto')}</span>
      {row('motto', 'bugHunt.room.motto')}

      <span className="tiny muted">{t('workflow.roomRoutine')}</span>
      <input
        className="input"
        maxLength={ROOM_LIMITS.title}
        value={texts.routineTitle ?? ''}
        placeholder={t('bugHunt.room.routineTitle')}
        onChange={(event) => onChange({ ...texts, routineTitle: event.target.value || null })}
      />
      {row('routine', 'bugHunt.room.routine')}

      {value && (
        <button type="button" className="btn btn--sm btn--ghost" style={{ alignSelf: 'flex-start' }} onClick={() => onChange(null)}>
          {t('workflow.roomReset')}
        </button>
      )}
    </div>
  )
}

function TelemetryTab() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { data } = useQuery({ queryKey: ['telemetry'], queryFn: api.settings.telemetry })
  const { data: plan } = useQuery({ queryKey: ['billing'], queryFn: api.billing.state, staleTime: 60_000 })
  const [draft, setDraft] = useState<TelemetrySettings | null>(null)

  const save = useMutation({
    mutationFn: (settings: TelemetrySettings) => api.settings.saveTelemetry(settings),
    onSuccess: () => {
      setDraft(null)
      void queryClient.invalidateQueries({ queryKey: ['telemetry'] })
    },
  })

  if (!data) return <Skeleton height={300} />
  const current = draft ?? data
  const update = (patch: Partial<TelemetrySettings>): void => setDraft({ ...current, ...patch })

  const toggles: Array<{ key: keyof TelemetrySettings; label: string; hint: string }> = [
    { key: 'collectDocumentSections', label: 'telemetry.collectDocumentSections', hint: 'telemetry.collectDocumentSectionsHint' },
    { key: 'collectFilePaths', label: 'telemetry.collectFilePaths', hint: 'telemetry.collectFilePathsHint' },
    { key: 'collectCommitMessages', label: 'telemetry.collectCommitMessages', hint: 'telemetry.collectCommitMessagesHint' },
    { key: 'collectTestNames', label: 'telemetry.collectTestNames', hint: 'telemetry.collectTestNamesHint' },
    { key: 'collectDocumentContent', label: 'telemetry.collectDocumentContent', hint: 'telemetry.collectDocumentContentHint' },
  ]

  return (
    <Panel title={t('settings.telemetry')}>
      <div className="stack stack--loose" style={{ maxWidth: 640 }}>
        <p className="secondary small">{t('telemetry.intro')}</p>

        <div className="stack">
          {toggles.map((toggle) => (
            <label key={toggle.key} className="checkbox">
              <input
                type="checkbox"
                checked={Boolean(current[toggle.key])}
                onChange={(e) => update({ [toggle.key]: e.target.checked } as Partial<TelemetrySettings>)}
              />
              <span className="checkbox__text">
                <span className="small">{t(toggle.label)}</span>
                <span className="tiny muted">{t(toggle.hint)}</span>
              </span>
            </label>
          ))}
        </div>

        {/*
          The most sensitive thing the product can collect, so it says exactly what happens:
          what is kept, for how long, who reads it, and that developers are told.
        */}
        <label className="checkbox">
          <input
            type="checkbox"
            checked={current.detectSensitiveData ?? true}
            onChange={(e) => update({ detectSensitiveData: e.target.checked })}
          />
          <span className="checkbox__text">
            <span className="small">{t('telemetry.detectSensitiveData')}</span>
            <span className="tiny muted">{t('telemetry.detectSensitiveDataHint')}</span>
          </span>
        </label>

        <label className="checkbox">
          <input
            type="checkbox"
            checked={current.showUsagePerPerson ?? false}
            onChange={(e) => update({ showUsagePerPerson: e.target.checked })}
          />
          <span className="checkbox__text">
            <span className="small">{t('telemetry.showUsagePerPerson')}</span>
            <span className="tiny muted">{t('telemetry.showUsagePerPersonHint')}</span>
          </span>
        </label>

        <div className={`prompt-policy${current.collectPromptText || current.collectAgentReplies || current.fullAgentLog ? ' prompt-policy--on' : ''}`}>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={current.collectPromptText ?? false}
              onChange={(e) => update({ collectPromptText: e.target.checked })}
            />
            <span className="checkbox__text">
              <span className="small">{t('telemetry.collectPromptText')}</span>
              <span className="tiny muted">{t('telemetry.collectPromptTextHint')}</span>
            </span>
          </label>
          {/* The agent's side of the same conversation, under the same promise and the same retention. */}
          <label className="checkbox">
            <input
              type="checkbox"
              checked={current.collectAgentReplies ?? false}
              onChange={(e) => update({ collectAgentReplies: e.target.checked })}
            />
            <span className="checkbox__text">
              <span className="small">{t('telemetry.collectAgentReplies')}</span>
              <span className="tiny muted">{t('telemetry.collectAgentRepliesHint')}</span>
            </span>
          </label>

          <LogLevelField full={current.fullAgentLog ?? false} allowed={plan?.entitlements.fullAgentLog === true} onChange={(fullAgentLog) => update({ fullAgentLog })} />

          {(current.collectPromptText || current.collectAgentReplies || current.fullAgentLog) && (
            <label className="field" style={{ maxWidth: 240 }}>
              <span className="field__label">{t('telemetry.promptRetentionDays')}</span>
              <input
                className="input"
                type="number"
                min={1}
                max={365}
                value={current.promptRetentionDays ?? 30}
                onChange={(e) => update({ promptRetentionDays: Number(e.target.value) })}
              />
            </label>
          )}
        </div>

        {/* The AI memory (docs/refactor/17): each person keeps it for their AIs; whether managers read it is the team's call. */}
        <label className="checkbox">
          <input
            type="checkbox"
            checked={current.managersReadAiMemory ?? true}
            onChange={(e) => update({ managersReadAiMemory: e.target.checked })}
          />
          <span className="checkbox__text">
            <span className="small">{t('telemetry.managersReadAiMemory')}</span>
            <span className="tiny muted">{t('telemetry.managersReadAiMemoryHint')}</span>
          </span>
        </label>

        <label className="field" style={{ maxWidth: 200 }}>
          <span className="field__label">{t('telemetry.retentionDays')}</span>
          <input
            className="input"
            type="number"
            min={0}
            max={3650}
            value={current.retentionDays}
            onChange={(e) => update({ retentionDays: Number(e.target.value) })}
          />
          <span className="field__hint">{t('telemetry.retentionHint')}</span>
        </label>

        <div className="stack stack--tight">
          <span className="field__label">{t('telemetry.sddPatterns')}</span>
          <span className="field__hint">{t('telemetry.sddPatternsHint')}</span>

          <table className="table">
            <thead>
              <tr>
                <th>{t('telemetry.pattern')}</th>
                <th>{t('telemetry.category')}</th>
              </tr>
            </thead>
            <tbody>
              {current.sddPatterns.map((pattern, index) => (
                <tr key={`${pattern.pattern}-${index}`}>
                  <td className="mono small">{pattern.pattern}</td>
                  <td className="small">{pattern.category}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button type="button" className="btn btn--primary" onClick={() => save.mutate(current)} disabled={save.isPending || !draft}>
          {save.isPending ? t('common.saving') : t('common.save')}
        </button>
      </div>
    </Panel>
  )
}
